import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { INITIAL_SHAPE_STATE } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { useDocumentTitle } from '@editor/hooks/useDocumentTitle'
import { InspectionArea, InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { Project, ProjectSheetItemsOrder } from '@editor/interfaces/project'
import { Shapes } from '@editor/interfaces/shape'
import { parseInspectionItemPartitionKey } from '@editor/services/InspectionArea'
import { isInspectionGroup } from '@editor/services/InspectionGroup'
import { debouncedUpdateInspectionItems, makeDefaultPartName } from '@editor/services/InspectionSheet'
import {
  getProjectInspectionItems,
  getProjectInspectionSheets,
  getProjectShapes,
  updateProject,
} from '@editor/services/Projects'
import { calculateRebarSurfaceArea, meterToMillimeter } from '@editor/services/Util'
import { decideActionPermission } from '@editor/services/Validation'
import { generateXLSX } from '@editor/services/exports/XLSX'
import { RootState, useAppDispatch } from '@editor/store/app'
import { reset as resetDiagram } from '@editor/store/diagram'
import { setProject } from '@editor/store/page'
import { debounce } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  patchInspectionItems,
  reset,
  resetEditedInspectionItemIds,
  resetEditedProject,
  setAllowedToModifyProject,
  setAllowedToModifySheet,
  setInspectionItems,
  setInspectionSheets,
  setIsSaving,
} from '../store'
import { isMainInspectionSheetItem } from '../utils'

const debouncedUpdateProject = debounce(
  async (
    getAccessToken: () => Promise<string>,
    project: Project,
    showErrorModal: (message: string) => void,
    beforeSaving: () => void,
    onSaved: (result: Project) => void,
    setIsSavingCb: (isSaving: boolean) => void,
  ) => {
    beforeSaving()
    setIsSavingCb(true)
    const token = await getAccessToken()
    if (!token) {
      return
    }

    const result = await updateProject(token, project, showErrorModal)
    if (!result) {
      return
    }

    onSaved(result)
    setIsSavingCb(false)
  },
  4000,
)

export default function useInspectionSheet() {
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Context
  const { showErrorModal } = useContext(GlobalModalContext)
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const editedProject = useSelector((state: RootState) => state.inspectionSheet.editedProject)
  const inspectionSheets = useSelector((state: RootState) => state.inspectionSheet.inspectionSheets)
  const inspectionItems = useSelector((state: RootState) => state.inspectionSheet.inspectionItems)
  const editedInspectionItemIds = useSelector((state: RootState) => state.inspectionSheet.editedInspectionItemIds)
  const areas = useSelector((state: RootState) => state.page.inspectionAreas)
  const area = useSelector((state: RootState) => state.page.inspectionArea)
  const diagrams = useSelector((state: RootState) => state.diagram.diagrams)
  const isOwner = useSelector((state: RootState) => state.page.isOwner)
  const isInvited = useSelector((state: RootState) => state.page.isInvited)
  const userType = useSelector((state: RootState) => state.user.userType)
  const isAllowedToModifySheet = useSelector((state: RootState) => state.inspectionSheet.isAllowedToModifySheet)
  const isAllowedToModifyProject = useSelector((state: RootState) => state.inspectionSheet.isAllowedToModifyProject)
  const settings = useSelector((state: RootState) => state.inspectionSheet.settings)
  const inspectionAreaIdToEdit = useSelector((state: RootState) => state.inspectionSheet.inspectionAreaIdToEdit)
  const isPageLoading = useSelector((state: RootState) => state.page.isLoading)
  const editedInspectionAreas = useSelector((state: RootState) => state.inspectionSheet.editedInspectionAreas)
  const editorPanelItems = useSelector((state: RootState) => state.inspectionGroup.groups)
  const inspectionGroups = editorPanelItems.map((item) => item.item).filter(isInspectionGroup)

  // States
  const [isLoading, setIsLoading] = useState(true)
  const [initComplete, setInitComplete] = useState(false)
  const [shapes, setShapes] = useState<Shapes>(INITIAL_SHAPE_STATE())

  // refs
  const settingsDrawerRef = useRef<{ openDrawer: () => void }>(null)

  // Summaries
  const rebarsSurfaceArea = useMemo(
    () => shapes.cylinders.reduce((sum, cylinder) => sum + calculateRebarSurfaceArea(cylinder), 0),
    [shapes.cylinders],
  )
  const totalVolume = useMemo(
    () =>
      inspectionItems
        .filter((item) => item.item_type === InspectionItemType.Volume)
        .reduce((total, item) => total + (item.volume?.estimated_value || 0), 0),
    [inspectionItems],
  )
  const totalArea = useMemo(
    () =>
      inspectionItems
        .filter((item) => item.item_type === InspectionItemType.PolygonArea && !item.volume_id)
        .reduce((total, item) => total + (item.polygon_area?.estimated_value || 0), 0),
    [inspectionItems],
  )
  const totalPolyline = useMemo(
    () =>
      meterToMillimeter(
        inspectionItems
          .filter((item) => item.item_type === InspectionItemType.PolylineLength)
          .reduce((total, item) => total + (item.polyline_length?.estimated_value || 0), 0),
      ),
    [inspectionItems],
  )

  /**
   * All additional metrics for any modeled item.
   * This will be used to instruct the other metrics to add padding.
   */
  const allAdditionalMetricsToggle = [
    !(settings.sheet_rows_visibility?.grid && settings.sheet_rows_visibility?.min_grid_depth) || false,
    !(settings.sheet_rows_visibility?.grid && settings.sheet_rows_visibility?.max_grid_depth) || false,
  ]

  /**
   * Sort inspection items by inspection area
   */
  const sortedInspectionItems = useMemo(() => {
    // Group by inspection area
    const grouped = inspectionItems
      .reduce<{ area: InspectionArea; groups: InspectionGroup[]; items: InspectionItem[] }[]>((collection, item) => {
        if (!item.partition_key) return collection

        const ids = parseInspectionItemPartitionKey(item.partition_key)
        if (ids.inspectionAreaId) {
          const ar = areas.find((a) => a.inspection_area_id === ids.inspectionAreaId)
          if (ar) {
            const areaIndex = collection.findIndex((c) => c.area.inspection_area_id === ar.inspection_area_id)
            if (areaIndex === -1) {
              collection.push({ area: ar, groups: [], items: [item] })
            } else {
              collection[areaIndex].items.push(item)
            }
          }
        }

        return collection
      }, [])
      // sort each items in the area according to settings
      .map((g) => {
        // before we sort, need to add default names for those items that don't have a name
        const count = new Map<InspectionItemType, number>([
          [InspectionItemType.Volume, -1],
          [InspectionItemType.PolygonArea, -1],
          [InspectionItemType.PolylineLength, -1],
          [InspectionItemType.LengthWithDistanceTool, -1],
        ])
        g.items = g.items.map((item) => {
          const updatedItem = { ...item }

          if (!isMainInspectionSheetItem(item)) {
            return updatedItem
          }

          // Ordering of surface polygon is stored on shape, not inspection item
          if (item.item_type === InspectionItemType.PolygonArea && !item.volume_id) {
            const shape = shapes.polygons.find((s) => s.shape_id === updatedItem.shape_ids.polygons[0])
            if (shape) {
              updatedItem.inspection_group_id = shape.inspection_group_id
              updatedItem.order_in_inspection_group = shape.order_in_inspection_group
            }
          }

          count.set(updatedItem.item_type!, (count.get(updatedItem.item_type!) || 0) + 1)
          return makeDefaultPartName(updatedItem, count.get(updatedItem.item_type!) || 0)
        })

        // if not using grouping but somehow order is set to InspectionGroup, return the group as is
        // it's already sorted by createdAt from server
        if (
          (!settings.sheet_use_inspection_group_order &&
            settings.sheet_order_type_of_items === ProjectSheetItemsOrder.CustomOrder) ||
          settings.sheet_order_type_of_items === ProjectSheetItemsOrder.CreatedAt
        ) {
          return g
        }

        // sort by name or order. there's no falling back to createdAt here since we already did that above.
        g.items.sort((a, b) => {
          if (settings.sheet_order_type_of_items === ProjectSheetItemsOrder.Name) {
            return a.part_name.localeCompare(b.part_name)
          }

          return (a.order_in_inspection_group || 0) - (b.order_in_inspection_group || 0)
        })

        return g
      })

    // Fill the list with areas that have no items
    areas.forEach((ar) => {
      if (!grouped.find((s) => s.area.inspection_area_id === ar.inspection_area_id)) {
        grouped.push({ area: ar, groups: [], items: [] })
      }
    })

    // Add groups to each area
    grouped.forEach((g) => {
      g.groups = editorPanelItems
        .filter((group) => isInspectionGroup(group.item) && group.item.inspection_area_id === g.area.inspection_area_id)
        .map((group) => group.item as InspectionGroup)
    })

    return grouped.sort((a, b) => {
      const editedA = editedInspectionAreas.find((edited) => edited.inspection_area_id === a.area.inspection_area_id)
      const editedB = editedInspectionAreas.find((edited) => edited.inspection_area_id === b.area.inspection_area_id)
      if (editedA && editedB) {
        return editedA.order! - editedB.order!
      }
      if (editedA) {
        return -1
      }
      if (editedB) {
        return 1
      }
      return a.area.order! - b.area.order!
    })
  }, [
    inspectionItems,
    editedInspectionAreas,
    areas,
    editorPanelItems,
    shapes.polygons,
    settings.sheet_order_type_of_items,
    settings.sheet_use_inspection_group_order,
  ])

  /**
   * Scroll to currently active inspection area
   */
  const keepTryingScrollToArea = useCallback(() => {
    const interval = setInterval(() => {
      const ele = document.getElementById(`inspection-area-${inspection_area_id}`)
      if (ele) {
        clearInterval(interval)
        ele.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }, 100)
  }, [inspection_area_id])

  /**
   * Set user permission to be used later
   */
  useEffect(() => {
    const permissionSet = decideActionPermission(isOwner, isInvited)
    dispatch(setAllowedToModifyProject(permissionSet.PROJECT_DASHBOARD.MODIFY.includes(userType)))
    dispatch(setAllowedToModifySheet(permissionSet.INSPECTION_SHEET.MODIFY.includes(userType)))
  }, [isOwner, isInvited, userType, dispatch])

  /**
   * Listen to changes in inspection items and update them on the server.
   */
  useEffect(() => {
    if (isAllowedToModifySheet && project && editedInspectionItemIds.length) {
      void debouncedUpdateInspectionItems(
        getAccessToken,
        project.project_id,
        inspectionItems.filter((i) => editedInspectionItemIds.includes(i.inspection_item_id!)),
        showErrorModal,
        // we reset the edited sheet ids before saving to immediately recognize any new changes while saving
        () => {
          dispatch(resetEditedInspectionItemIds())
        },
        (results) => {
          dispatch(resetEditedInspectionItemIds())
          dispatch(patchInspectionItems(results))
        },
        (flag) => dispatch(setIsSaving(flag)),
      )
    }
  }, [
    inspectionItems,
    editedInspectionItemIds,
    project,
    isAllowedToModifySheet,
    dispatch,
    getAccessToken,
    showErrorModal,
  ])

  /**
   * Listen to changes in project and update them on the server.
   */
  useEffect(() => {
    if (isAllowedToModifySheet && editedProject) {
      void debouncedUpdateProject(
        getAccessToken,
        editedProject,
        showErrorModal,
        () => null,
        (result) => {
          dispatch(setProject(result))
          dispatch(resetEditedProject())
        },
        (flag) => dispatch(setIsSaving(flag)),
      )
    }
  }, [editedProject, isAllowedToModifySheet, dispatch, getAccessToken, showErrorModal])

  /**
   * Initial fetch of inspection items and shapes
   */
  useEffect(() => {
    if (!project || isPageLoading || initComplete) return () => null

    const controller = new AbortController()

    void (async ({ project: prj }: { project: Project }) => {
      if (!prj) return

      setIsLoading(true)

      const token = await getAccessToken()
      if (!token) {
        setIsLoading(false)
        return
      }

      // Get inspection items and shapes
      const results = await Promise.all([
        getProjectInspectionSheets(token, prj.project_id, showErrorModal, controller.signal),
        getProjectInspectionItems(token, prj.project_id, showErrorModal, controller.signal),
        getProjectShapes(token, prj.project_id, showErrorModal, controller.signal),
      ])
      const [shts, its, shps] = results

      if (shps !== false) setShapes(shps || INITIAL_SHAPE_STATE())
      if (its !== false) dispatch(setInspectionItems(its || []))
      if (shts !== false) dispatch(setInspectionSheets(shts))
      if (!results.some((r) => r === false)) {
        setIsLoading(false)
        setInitComplete(true)
      }
    })({ project })

    mixpanel.track_pageview({
      Page: 'Inspection Sheet',
      'Project ID': project.project_id,
      'Inspection Area ID': inspection_area_id,
    })

    return () => {
      controller.abort()
    }
  }, [project, isPageLoading, initComplete, getAccessToken, showErrorModal, dispatch, inspection_area_id])

  /**
   * Generate a safe file name for exports, with all special characters removed and without file extension.
   */
  const exportFileName = useMemo(
    // The export contains the whole project's data, so the file name is not scoped to an area.
    () => `${project?.project_name || ''}`.replaceAll(/[<>:"/\\|?*]+/g, '_'),
    [project?.project_name],
  )

  /**
   * Generate XLSX file
   */
  const handleGenerateXLSX = useCallback(
    async (bleedingEdgeItems: InspectionItem[]) => {
      if (!project || !sortedInspectionItems.map((s) => s.area).length) {
        return
      }

      await generateXLSX(
        exportFileName,
        project,
        sortedInspectionItems.map((s) => s.area),
        inspectionSheets,
        bleedingEdgeItems,
        inspectionGroups,
        shapes,
        diagrams,
        true,
      )

      // Tracking
      mixpanel.track('Export inspection sheet', { 'File format': 'XLSX' })
    },
    [project, sortedInspectionItems, inspectionSheets, shapes, inspectionGroups, diagrams, exportFileName],
  )

  /**
   * If an inspection area is marked for editing, open the drawer.
   */
  useEffect(() => {
    if (inspectionAreaIdToEdit) {
      settingsDrawerRef.current?.openDrawer()
    }
  }, [inspectionAreaIdToEdit])

  /**
   * Reset store on unmount
   */
  useEffect(
    () => () => {
      dispatch(resetDiagram())
      dispatch(reset())
    },
    [dispatch],
  )

  /**
   * Set document title
   */
  useDocumentTitle(
    [
      !area && !project ? '3D InfraLoop' : null,
      area ? area.inspection_area_name : null,
      project ? project.project_name : null,
    ]
      .filter(Boolean)
      .join(' · '),
  )

  /**
   * If area has already been set and inspection_area_id is changed, scroll to area.
   */
  useEffect(() => {
    if (inspection_area_id) {
      keepTryingScrollToArea()
    }
  }, [inspection_area_id, keepTryingScrollToArea])

  /**
   * Mixpanel super properties
   */
  const mixpanelSuperPropsRef = useRef({})
  useEffect(() => {
    mixpanelSuperPropsRef.current = {
      Page: 'Inspection Sheet',
      'Project ID': project?.project_id,
      'Inspection Area ID': inspection_area_id,
    }
    mixpanel.register(mixpanelSuperPropsRef.current)
  }, [project, inspection_area_id])

  // Cleanup super properties on unmount
  useEffect(() => () => Object.keys(mixpanelSuperPropsRef.current).forEach((prop) => mixpanel.unregister(prop)), [])

  return {
    handleGenerateXLSX,
    isLoading,
    shapes,
    isAllowedToModifyProject,
    isAllowedToModifySheet,
    allAdditionalMetricsToggle,
    sortedInspectionItems,
    rebarsSurfaceArea,
    totalVolume,
    totalArea,
    totalPolyline,
    settingsDrawerRef,
  }
}
